import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FileText, Send, RefreshCw, Users, DollarSign, Plane, UserCheck, Clock, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export function DailySummaryTab({ summary, loading, onFetch, onSendWhatsApp }) {
  const [adminPhone, setAdminPhone] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false, admin_phone: "", send_time: "07:00", timezone: "America/Chicago", last_sent: null
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/daily-summary/settings`, getAuthHeaders());
      setScheduleSettings(res.data);
    } catch (e) { /* settings not configured yet */ }
  };

  const saveSettings = async () => {
    if (scheduleSettings.enabled && !scheduleSettings.admin_phone) {
      toast.error("Please enter admin WhatsApp number to enable auto-summary");
      return;
    }
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/daily-summary/settings`, {
        enabled: scheduleSettings.enabled,
        admin_phone: scheduleSettings.admin_phone,
        send_time: scheduleSettings.send_time,
        timezone: scheduleSettings.timezone
      }, getAuthHeaders());
      toast.success(scheduleSettings.enabled ? "Auto daily summary enabled!" : "Auto daily summary disabled");
    } catch (e) { toast.error("Failed to save settings"); }
    setSavingSettings(false);
  };

  return (
    <div className="space-y-6" data-testid="daily-summary-tab">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h3 className="text-lg font-bold">Daily Operations Summary</h3>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
            data-testid="summary-date-picker"
          />
          <Button variant="outline" size="sm" onClick={() => onFetch(selectedDate)} disabled={loading} data-testid="fetch-summary-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? "Loading..." : "Generate"}
          </Button>
        </div>
      </div>

      {/* Auto-Scheduler Settings */}
      <Card className={scheduleSettings.enabled ? "border-green-300 bg-green-50/30" : "border-gray-200"} data-testid="auto-schedule-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-sm">Automated Daily Summary</span>
              {scheduleSettings.enabled && (
                <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="auto-summary-toggle" className="text-xs text-muted-foreground">
                {scheduleSettings.enabled ? "On" : "Off"}
              </Label>
              <Switch
                id="auto-summary-toggle"
                checked={scheduleSettings.enabled}
                onCheckedChange={(checked) => setScheduleSettings(prev => ({ ...prev, enabled: checked }))}
                data-testid="auto-summary-toggle"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">WhatsApp Number</Label>
              <Input
                placeholder="+18159148139"
                value={scheduleSettings.admin_phone}
                onChange={(e) => setScheduleSettings(prev => ({ ...prev, admin_phone: e.target.value }))}
                className="mt-1"
                data-testid="schedule-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Send Time</Label>
              <Input
                type="time"
                value={scheduleSettings.send_time}
                onChange={(e) => setScheduleSettings(prev => ({ ...prev, send_time: e.target.value }))}
                className="mt-1"
                data-testid="schedule-time-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Timezone</Label>
              <select
                value={scheduleSettings.timezone}
                onChange={(e) => setScheduleSettings(prev => ({ ...prev, timezone: e.target.value }))}
                className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="schedule-timezone-select"
              >
                <option value="America/Chicago">Central (Chicago)</option>
                <option value="America/New_York">Eastern (New York)</option>
                <option value="America/Denver">Mountain (Denver)</option>
                <option value="America/Los_Angeles">Pacific (LA)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-muted-foreground">
              {scheduleSettings.last_sent
                ? `Last sent: ${scheduleSettings.last_sent}`
                : "No summary sent yet"}
              {scheduleSettings.enabled && " | Twilio sandbox: recipient must join first"}
            </div>
            <Button size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="save-schedule-btn">
              <Settings className="w-3 h-3 mr-1" />
              {savingSettings ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!summary ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No summary generated yet</p>
          <p className="text-sm">Select a date and click "Generate" to view the daily report</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                <p className="text-2xl font-bold">{summary.total_passengers}</p>
                <p className="text-xs text-muted-foreground">Total Passengers</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Plane className="w-6 h-6 mx-auto mb-1 text-green-500" />
                <p className="text-2xl font-bold">{summary.arrivals}</p>
                <p className="text-xs text-muted-foreground">Arrivals</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Plane className="w-6 h-6 mx-auto mb-1 text-orange-500 rotate-180" />
                <p className="text-2xl font-bold">{summary.returns}</p>
                <p className="text-xs text-muted-foreground">Returns</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                <p className="text-2xl font-bold">${summary.total_expected?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Expected Revenue</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Booking Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Individual Bookings</span>
                  <Badge variant="outline">{summary.total_individual_bookings}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Group Bookings</span>
                  <Badge variant="outline">{summary.total_group_bookings}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Cancelled</span>
                  <Badge variant="destructive">{summary.cancelled}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Payment Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Paid</span>
                  <Badge className="bg-green-100 text-green-700 border-green-200">{summary.paid_count}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Pending</span>
                  <Badge variant="secondary">{summary.pending_count}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Cash Payments</span>
                  <Badge variant="outline">{summary.cash_payments}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Stripe Payments</span>
                  <Badge variant="outline">{summary.stripe_payments}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Financial Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Expected</span>
                  <span className="font-semibold">${summary.total_expected?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Collected</span>
                  <span className="font-semibold text-green-600">${summary.total_paid_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Balance Due</span>
                  <span className="font-semibold text-red-600">${summary.total_balance_due?.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Flight Groups</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm max-h-40 overflow-y-auto">
                {Object.keys(summary.flight_groups || {}).length === 0 ? (
                  <p className="text-muted-foreground">No flights for this date</p>
                ) : (
                  Object.entries(summary.flight_groups).map(([fn, info]) => (
                    <div key={fn} className="flex justify-between items-center">
                      <div>
                        <span className="font-mono font-semibold">{fn}</span>
                        <span className="text-muted-foreground ml-2">({info.names?.slice(0, 2).join(", ")}{info.names?.length > 2 ? ` +${info.names.length - 2}` : ""})</span>
                      </div>
                      <Badge variant="outline">{info.passengers} pax</Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <UserCheck className="w-4 h-4" /> Driver Assignments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.keys(summary.driver_assignments || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No driver assignments for this date</p>
                ) : (
                  Object.entries(summary.driver_assignments).map(([driver, count]) => (
                    <Badge key={driver} variant="outline" className="text-sm py-1 px-3">
                      {driver}: {count} trip{count !== 1 ? 's' : ''}
                    </Badge>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <p className="text-sm font-semibold mb-3">Send Summary via WhatsApp (One-time)</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Admin WhatsApp number (e.g. +18159148139)"
                  value={adminPhone}
                  onChange={(e) => setAdminPhone(e.target.value)}
                  className="flex-1"
                  data-testid="summary-admin-phone"
                />
                <Button
                  onClick={() => onSendWhatsApp(adminPhone)}
                  disabled={loading || !adminPhone}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="send-summary-whatsapp-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Sending..." : "Send"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Note: Twilio sandbox mode requires the recipient to have joined the sandbox first.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
